import React, { useEffect, useState } from "react";
import { getThiKhaoSatSheet, fetchThiKhaoSatFile, uploadThiKhaoSatFile } from "./happyschoolApi";
import { gradeSheet, normP3 } from "../quiz3_static/thpt3Grade";

// Hồ sơ chấm phiếu 1 thí sinh / 1 môn (bấm đúp ô điểm ở tab Nhập điểm của Thi khảo sát):
//   Đề thi của học sinh · Bài làm của học sinh (ảnh scan) · Đáp án của học sinh · Đáp án của mã đề.
const TABS = [
  { key: "de", label: "📄 Đề thi của học sinh" },
  { key: "bailam", label: "🖼️ Bài làm của học sinh" },
  { key: "scan", label: "📑 Phiếu scan (PDF)" },
  { key: "hs", label: "✍️ Đáp án của học sinh" },
  { key: "key", label: "✅ Đáp án của mã đề" },
];
const ABCD = ["a", "b", "c", "d"];
const ds = (v) => (v === true ? "Đ" : v === false ? "S" : v === "multi" ? "*" : "");
const fmt = (n) => (n == null ? "" : String(n).replace(".", ","));

function useBlobUrl(schoolId, examId, fileId) {
  const [st, setSt] = useState({ url: "", err: "", loading: false });
  useEffect(() => {
    if (!fileId) { setSt({ url: "", err: "", loading: false }); return undefined; }
    let url = ""; let alive = true;
    setSt({ url: "", err: "", loading: true });
    fetchThiKhaoSatFile(schoolId, examId, fileId)
      .then((b) => { url = URL.createObjectURL(b); if (alive) setSt({ url, err: "", loading: false }); })
      .catch((e) => alive && setSt({ url: "", err: e.message, loading: false }));
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [schoolId, examId, fileId]);
  return st;
}

// Thanh công cụ trên PDF: mở tab mới (trình duyệt tự có nút phóng to/in) + tải file về máy.
function PdfBar({ url, fileName, page, note }) {
  const btn = { fontSize: 13, border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 10px", background: "#fff", color: "#111827", textDecoration: "none", whiteSpace: "nowrap" };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 14px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
      <a href={page ? `${url}#page=${page}` : url} target="_blank" rel="noreferrer" style={btn}>🔍 Mở tab mới</a>
      <a href={url} download={fileName} style={{ ...btn, background: "#0f766e", borderColor: "#0f766e", color: "#fff" }}>⬇ Tải PDF về</a>
      <span style={{ fontSize: 12, color: "#6b7280" }}>{fileName}{note ? ` — ${note}` : ""}</span>
    </div>
  );
}

function Upload({ label, accept, onFile, busy }) {
  return (
    <label style={{ display: "inline-block", fontSize: 13, border: "1px dashed #9ca3af", borderRadius: 6, padding: "6px 10px", cursor: "pointer", marginTop: 8 }}>
      {busy ? "Đang tải lên..." : label}
      <input type="file" accept={accept} disabled={busy} style={{ display: "none" }} onChange={(e) => { const f = (e.target.files || [])[0]; e.target.value = ""; if (f) onFile(f); }} />
    </label>
  );
}

function KeyTable({ sheet, answerKey, mode }) {
  // mode "hs": đáp án HS tô (tô màu đúng/sai theo đáp án); "key": đáp án mã đề
  const p1n = Math.max((answerKey?.p1 || []).reduce((m, v, i) => (v ? i + 1 : m), 0), mode === "hs" ? (sheet?.part1 || []).reduce((m, v, i) => (v ? i + 1 : m), 0) : 0);
  const p2q = [...Array(8).keys()].filter((q) => ABCD.some((x) => (answerKey?.p2?.[q] || {})[x] != null) || (mode === "hs" && ABCD.some((x) => (sheet?.part2?.[q] || {})[x] != null)));
  const p3n = Math.max((answerKey?.p3 || []).reduce((m, v, i) => (v ? i + 1 : m), 0), mode === "hs" ? (sheet?.part3 || []).reduce((m, v, i) => (v ? i + 1 : m), 0) : 0);
  const cell = (ok, has) => ({ border: "1px solid #e5e7eb", padding: "4px 6px", textAlign: "center", minWidth: 30, fontWeight: 600,
    background: mode !== "hs" || !answerKey ? "#fff" : !has ? "#f3f4f6" : ok ? "#dcfce7" : "#fee2e2", color: mode === "hs" && answerKey && has && !ok ? "#b91c1c" : "#111827" });
  const head = { border: "1px solid #e5e7eb", padding: "4px 6px", background: "#f3f4f6", fontSize: 12, textAlign: "center" };
  const same3 = (a, b) => { const x = normP3(a), y = normP3(b); return x != null && y != null && !(x && x.bad) && (typeof x === "number" && typeof y === "number" ? Math.abs(x - y) < 1e-9 : String(x) === String(y)); };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {p1n > 0 && (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Phần I — Trắc nghiệm nhiều lựa chọn</div>
          <div style={{ overflowX: "auto" }}><table style={{ borderCollapse: "collapse", fontSize: 13 }}><tbody>
            <tr><td style={head}>Câu</td>{[...Array(p1n).keys()].map((i) => <td key={i} style={head}>{i + 1}</td>)}</tr>
            {mode === "hs" && <tr><td style={head}>HS tô</td>{[...Array(p1n).keys()].map((i) => { const v = sheet?.part1?.[i]; const k = answerKey?.p1?.[i]; return <td key={i} style={cell(v === k, !!v)}>{v === "multi" ? "*" : v || "–"}</td>; })}</tr>}
            <tr><td style={head}>Đáp án</td>{[...Array(p1n).keys()].map((i) => <td key={i} style={{ ...cell(true, true), background: "#fff", color: "#047857" }}>{answerKey?.p1?.[i] || ""}</td>)}</tr>
          </tbody></table></div>
        </div>
      )}
      {p2q.length > 0 && (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Phần II — Đúng / Sai</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {p2q.map((q) => (
              <table key={q} style={{ borderCollapse: "collapse", fontSize: 13 }}><tbody>
                <tr><td style={head} colSpan={mode === "hs" ? 3 : 2}>Câu {q + 1}</td></tr>
                <tr><td style={head}>Ý</td>{mode === "hs" && <td style={head}>HS</td>}<td style={head}>Đ.án</td></tr>
                {ABCD.map((x) => { const v = sheet?.part2?.[q]?.[x]; const k = answerKey?.p2?.[q]?.[x]; return (
                  <tr key={x}><td style={head}>{x})</td>{mode === "hs" && <td style={cell(v === k, v != null)}>{ds(v) || "–"}</td>}<td style={{ ...cell(true, true), background: "#fff", color: "#047857" }}>{ds(k)}</td></tr>
                ); })}
              </tbody></table>
            ))}
          </div>
        </div>
      )}
      {p3n > 0 && (
        <div>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Phần III — Trả lời ngắn</div>
          <table style={{ borderCollapse: "collapse", fontSize: 13 }}><tbody>
            <tr><td style={head}>Câu</td>{[...Array(p3n).keys()].map((i) => <td key={i} style={head}>{i + 1}</td>)}</tr>
            {mode === "hs" && <tr><td style={head}>HS tô</td>{[...Array(p3n).keys()].map((i) => { const v = sheet?.part3?.[i]; return <td key={i} style={cell(same3(v, answerKey?.p3?.[i]), !!v)}>{v || "–"}</td>; })}</tr>}
            <tr><td style={head}>Đáp án</td>{[...Array(p3n).keys()].map((i) => <td key={i} style={{ ...cell(true, true), background: "#fff", color: "#047857" }}>{answerKey?.p3?.[i] || ""}</td>)}</tr>
          </tbody></table>
        </div>
      )}
    </div>
  );
}

export default function ThiKhaoSatSheetModal({ schoolId, examId, subjectKey, subjectLabel, classId, student, canEdit, onClose }) {
  const [tab, setTab] = useState("bailam");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(false);
  const load = () => getThiKhaoSatSheet(schoolId, examId, { subjectKey, classId, studentId: student.id }).then(setD).catch((e) => setErr(e.message));
  useEffect(() => { setD(null); setErr(""); load(); }, [schoolId, examId, subjectKey, classId, student.id]); // eslint-disable-line
  useEffect(() => { const k = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const sheet = d?.sheet || null;
  const maDe = sheet?.maDe || "";
  const de = useBlobUrl(schoolId, examId, tab === "de" ? d?.deFile?.fileId : null);
  const img = useBlobUrl(schoolId, examId, tab === "bailam" ? sheet?.imageId : null);
  const scan = useBlobUrl(schoolId, examId, tab === "scan" ? d?.scanFile?.fileId : null);
  // Phiếu chưa có ảnh riêng nhưng đã có file PDF scan của phòng -> mở thẳng tab PDF.
  useEffect(() => { if (d && !d.sheet?.imageId && d.scanFile) setTab((t) => (t === "bailam" ? "scan" : t)); }, [d]);
  const g = sheet && d?.answerKey ? gradeSheet(sheet, { [maDe]: d.answerKey }) : null;
  // Danh sách phòng thi đã ký (file scan có chữ "danh_sach" trong tên): mở tab mới hoặc tải về.
  const lists = Object.entries(d?.scanFiles || {}).filter(([n]) => /danh_sach/i.test(n)).map(([fileName, v]) => ({ fileName, fileId: v.fileId }));
  const [listBusy, setListBusy] = useState("");
  const openList = async (f, download) => {
    const w = download ? null : window.open("", "_blank"); // mở cửa sổ ngay khi bấm để trình duyệt không chặn popup
    setListBusy(f.fileName); setErr("");
    try {
      const url = URL.createObjectURL(new Blob([await fetchThiKhaoSatFile(schoolId, examId, f.fileId)], { type: "application/pdf" }));
      if (download) { const a = document.createElement("a"); a.href = url; a.download = f.fileName; document.body.appendChild(a); a.click(); a.remove(); } else if (w) w.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (e) { if (w) w.close(); setErr(e.message); } finally { setListBusy(""); }
  };
  const upload = (params, f) => { setBusy(true); setErr(""); uploadThiKhaoSatFile(schoolId, examId, params, f).then(load).catch((e) => setErr(e.message)).finally(() => setBusy(false)); };

  const box = { position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 12 };
  const panel = { background: "#fff", borderRadius: 10, width: "min(1100px, 100%)", height: "min(92vh, 1000px)", display: "flex", flexDirection: "column", boxShadow: "0 20px 50px rgba(0,0,0,.3)" };
  const tabBtn = (on) => ({ padding: "8px 12px", border: "none", borderBottom: on ? "3px solid #0f766e" : "3px solid transparent", background: "transparent", fontWeight: on ? 700 : 500, color: on ? "#0f766e" : "#374151", cursor: "pointer", fontSize: 14, whiteSpace: "nowrap" });
  const empty = { color: "#6b7280", fontSize: 14, padding: 16 };

  return (
    <div style={box} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "12px 16px 6px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{student.name} — {subjectLabel}</div>
            <div style={{ fontSize: 13, color: "#4b5563" }}>
              SBD <b>{d?.sbd || "?"}</b>{maDe && <> · Mã đề <b>{maDe}</b></>}
              {d && <> · Điểm <b style={{ color: "#0f766e" }}>{fmt(d.score) || "—"}</b></>}
              {g && g.ok && <> (I: {fmt(g.diem1)} · II: {fmt(g.diem2)} · III: {fmt(g.diem3)})</>}
              {sheet?.sourceFile && <> · {sheet.sourceFile}, trang {sheet.page}</>}
            </div>
            {d?.note && <div style={{ fontSize: 13, color: "#b45309", marginTop: 2 }}>📝 {d.note}</div>}
          </div>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 22, cursor: "pointer", lineHeight: 1 }} title="Đóng (Esc)">×</button>
        </div>
        <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #e5e7eb", padding: "0 10px", overflowX: "auto" }}>
          {TABS.map((t) => <button key={t.key} type="button" style={tabBtn(tab === t.key)} onClick={() => setTab(t.key)}>{t.label}</button>)}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: tab === "de" || tab === "scan" ? 0 : 14 }}>
          {err && <div style={{ color: "#b91c1c", fontSize: 14, padding: 8 }}>{err}</div>}
          {!d && !err && <div style={empty}>Đang tải...</div>}
          {d && tab === "de" && (
            !maDe ? <div style={empty}>Chưa có bài làm nên chưa biết mã đề của thí sinh.</div>
              : d.deFile ? (de.url ? <div style={{ display: "flex", flexDirection: "column", height: "100%" }}><PdfBar url={de.url} fileName={d.deFile.fileName || `Ma_de_${maDe}.pdf`} /><iframe title="Đề thi" src={de.url} style={{ width: "100%", flex: 1, border: 0 }} /></div> : <div style={empty}>{de.err || "Đang tải đề..."}</div>)
                : <div style={empty}>Chưa tải đề mã {maDe} lên.<br />{canEdit && <Upload label={`⬆ Tải đề mã ${maDe} (PDF)`} accept="application/pdf" busy={busy} onFile={(f) => upload({ kind: "de", subjectKey, maDe }, f)} />}</div>
          )}
          {d && tab === "bailam" && (
            sheet?.imageId ? (img.url
              ? <img src={img.url} alt="Bài làm" onClick={() => setZoom((z) => !z)} title="Bấm để phóng to / thu nhỏ" style={{ display: "block", margin: "0 auto", width: zoom ? "160%" : "100%", maxWidth: zoom ? "none" : 820, cursor: zoom ? "zoom-out" : "zoom-in", border: "1px solid #e5e7eb" }} />
              : <div style={empty}>{img.err || "Đang tải ảnh..."}</div>)
              : <div style={empty}>Chưa có ảnh bài làm.{d.scanFile && <> Xem tab <b>📑 Phiếu scan (PDF)</b> để xem / tải file PDF gốc.</>}<br />{canEdit && d.sbd && <Upload label="⬆ Tải ảnh bài làm (JPG/PNG)" accept="image/*" busy={busy} onFile={(f) => upload({ kind: "bailam", subjectKey, sbd: d.sbd }, f)} />}</div>
          )}
          {d && tab === "scan" && lists.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 14px", borderBottom: "1px solid #e5e7eb", background: "#fffbeb" }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>📋 Danh sách phòng thi đã ký:</span>
              {lists.map((f) => (
                <span key={f.fileName} style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "#6b7280" }}>{f.fileName}</span>
                  <button type="button" disabled={!!listBusy} onClick={() => openList(f, false)} style={{ fontSize: 12, border: "1px solid #d1d5db", borderRadius: 6, padding: "3px 8px", background: "#fff", cursor: "pointer" }}>{listBusy === f.fileName ? "Đang tải..." : "Mở"}</button>
                  <button type="button" disabled={!!listBusy} onClick={() => openList(f, true)} style={{ fontSize: 12, border: "1px solid #0f766e", borderRadius: 6, padding: "3px 8px", background: "#0f766e", color: "#fff", cursor: "pointer" }}>Tải về</button>
                </span>
              ))}
            </div>
          )}
          {d && tab === "scan" && (
            !sheet?.sourceFile ? <div style={empty}>Chưa biết phiếu này nằm ở file scan nào (bài làm chưa được đẩy từ "Chấm cả phòng").</div>
              : d.scanFile ? (scan.url
                ? <div style={{ display: "flex", flexDirection: "column", height: "100%" }}><PdfBar url={scan.url} fileName={d.scanFile.fileName || sheet.sourceFile} page={sheet.page} note={`phiếu này ở trang ${sheet.page || 1}`} /><iframe title="Phiếu scan" src={`${scan.url}#page=${sheet.page || 1}`} style={{ width: "100%", flex: 1, border: 0 }} /></div>
                : <div style={empty}>{scan.err || "Đang tải file scan..."}</div>)
                : <div style={empty}>Chưa tải file scan <b>{sheet.sourceFile}</b> lên.<br />{canEdit && <Upload label={`⬆ Tải file scan ${sheet.sourceFile} (PDF)`} accept="application/pdf" busy={busy} onFile={(f) => upload({ kind: "scan", subjectKey, fileName: sheet.sourceFile }, f)} />}</div>
          )}
          {d && tab === "hs" && (
            !sheet?.part1 ? <div style={empty}>Chưa có dữ liệu đọc phiếu (chấm bằng "Chấm cả phòng" rồi đẩy điểm vào kỳ thi).</div> : (
              <>
                {!d.answerKey && <div style={{ ...empty, padding: "0 0 8px" }}>Chưa có đáp án mã đề {maDe} — chỉ hiện bài tô, không tô màu đúng/sai.</div>}
                {g && g.ok && <div style={{ fontSize: 13, marginBottom: 10 }}>Phần I đúng <b>{g.p1Correct}/{g.p1Count}</b> · Phần II số ý đúng mỗi câu: <b>{g.p2Detail.join(" · ")}</b> · Phần III đúng <b>{g.p3Correct}/{g.p3Count}</b> → <b style={{ color: "#0f766e" }}>{fmt(g.total)} điểm</b></div>}
                {sheet.warnings?.length > 0 && <div style={{ fontSize: 13, color: "#b45309", marginBottom: 10 }}>⚠️ {sheet.warnings.join("; ")}</div>}
                <KeyTable sheet={sheet} answerKey={d.answerKey} mode="hs" />
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 10 }}>Xanh = đúng, đỏ = sai, xám = bỏ trống, * = tô nhiều ô.</div>
              </>
            )
          )}
          {d && tab === "key" && (
            d.answerKey ? <><div style={{ fontSize: 13, color: "#4b5563", marginBottom: 10 }}>Mã đề <b>{maDe}</b>{d.answerKeyFile && <> — từ file {d.answerKeyFile}</>}</div><KeyTable answerKey={d.answerKey} mode="key" /></>
              : <div style={empty}>{maDe ? `Chưa lưu đáp án mã đề ${maDe} cho môn này.` : "Chưa biết mã đề của thí sinh."}</div>
          )}
        </div>
      </div>
    </div>
  );
}
